<template>
  <VCalendarDatePicker
    v-model.range="date"
    :rows="isLargeScreen ? 1 : 2"
    :columns="isLargeScreen ? 2 : 1"
    v-bind="{ ...attrs, ...$attrs, attributes }"
    :disabled-dates="filteredDisableDates"
    :expanded="!isLargeScreen"
    @dayclick="onDayClick"
    @drag="onStartSelected($event)"
  >
    <template #footer>
      <div class="w-full mb-3 flex justify-center">
        <slot />
      </div>
    </template>
  </VCalendarDatePicker>
</template>

<script setup lang="ts">
import { DatePicker as VCalendarDatePicker } from 'v-calendar'
import type { DatePickerRangeObject } from 'v-calendar/dist/types/src/use/datePicker.js'
import 'v-calendar/dist/style.css'
import { LangList } from '~/modules/lang/entities/Lang'

const { isLargeScreen } = useResponsive()

defineOptions({ inheritAttrs: false })

interface propsInterface {
  modelValue?: DatePickerRangeObject | null
  disableDates?: Array<{ start: string; end: string }> | undefined
}

const props = withDefaults(defineProps<propsInterface>(), {
  modelValue: null,
})

const selectedStartDate = ref<Date | null>(null)

const emit = defineEmits(['update:model-value', 'close', 'invalid-range'])

const onStartSelected = (dates: DatePickerRangeObject) => {
  selectedStartDate.value = dates.start as Date
}

const filteredDisableDates = computed(() => {
  if (!selectedStartDate.value || !props.disableDates?.length) {
    return props.disableDates
  }

  const startDate = new Date(selectedStartDate.value)

  let firstDisabledAfterStart: { start: string; end: string } | undefined

  for (const d of props.disableDates) {
    const disabledDate = new Date(d.start)
    if (disabledDate > startDate) {
      firstDisabledAfterStart = d
      break
    }
  }

  return firstDisabledAfterStart
    ? props.disableDates.filter(d => d.start !== firstDisabledAfterStart.start)
    : props.disableDates
})

const attributes = ref([
  {
    key: 'today',
    dates: new Date(),
  },
  {
    key: 'disabled-custom',
    highlight: { fillMode: 'light', color: 'red' },
    content: 'red',
    dates: filteredDisableDates,
  },
])
const validateRange = (value: DatePickerRangeObject) => {
  if (value.start && value.end) {
    const startDate = new Date(value.start as Date)
    const endDate = new Date(value.end as Date)

    if (startDate.getTime() === endDate.getTime()) {
      return {
        start: startDate,
        end: new Date(startDate.getTime() + 24 * 60 * 60 * 1000),
      }
    }

    return { start: startDate, end: endDate }
  }
  return value
}

const date = computed({
  get: () => props.modelValue,
  set: value => {
    const validatedValue = validateRange(value as DatePickerRangeObject)
    emit('update:model-value', validatedValue)
    emit('close')
  },
})

const attrs = {
  transparent: true,
  borderless: true,
  color: 'primary',
  locale: LangList['es-ES'],
  'first-day-of-week': 2,
  'min-date': new Date(),
  'max-date': new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
}

function onDayClick(_: any, event: MouseEvent): void {
  const target = event.target as HTMLElement
  target.blur()
}
</script>

<style lang="postcss">
.vc-pane-container {
  @apply font-sans;
}

.vc-header {
  @apply mb-2.5;
}

.vc-header .vc-title {
  @apply text-gray-900 text-base font-bold;
}

.vc-weekday {
  @apply text-gray-900 text-xs font-bold;
}

.vc-weeks {
  @apply px-5;
}

.vc-nav-item {
  @apply text-gray-900;
}

.vc-nav-item.is-active {
  @apply text-gray-900;
}

.vc-day-content {
  @apply rounded text-sm font-normal bg-transparent text-gray-900;
}

.vc-day-content:hover {
  @apply font-bold border-2 border-solid border-primary-600;
}

.vc-disabled:hover {
  @apply font-normal border-none;
}

.vc-highlight-content-outline {
  @apply font-bold bg-primary-800 text-white;
}

.vc-highlight-content-outline:hover {
  @apply font-bold bg-primary-800 text-white;
}

.vc-highlight-content-solid {
  @apply font-bold bg-primary-800 text-white;
}

.vc-highlight-content-solid:hover {
  @apply font-bold bg-primary-800 text-white;
}

.vc-highlight-bg-outline {
  @apply hidden;
}

.vc-highlight-content-light {
  @apply font-bold bg-primary-200 text-primary-900;
}

.vc-day-content.vc-disabled {
  @apply bg-gray-100 text-gray-400;
}

.vc-disabled.vc-red {
  @apply font-normal bg-red-100 text-red-700 line-through;
}
</style>
